/**
 * List page size — the single place a web list's rows-per-page is written down.
 *
 * Before this module each list carried its own literal next to the component
 * that used it (People Lists 5, Searches 20, the contacts table 100), so
 * changing "how many rows per page" meant finding every copy and the one that
 * got missed was found by a recruiter rather than by CI.
 *
 * The value resolves PER STAGE rather than being one global constant. If every
 * environment showed 100 rows, no pagination bug would ever reproduce below
 * production, because no seeded local dataset is big enough to produce a second
 * page. Keeping the lower tiers small means the pager stays on screen where we
 * actually test.
 *
 * The stage is NOT "which machine am I on". It is `import.meta.env.VITE_APP_STAGE`,
 * and WHO sets it differs per build. `packages/web/vite.config.ts` resolves
 * `process.env.VITE_APP_STAGE ?? .env.local's VITE_APP_STAGE ?? "local"` and
 * `define`s the result — so a laptop's browser shows **20** when its `.env.local`
 * omits the key or says `local`, and **50** when it says `dev`, which is what
 * `.env.local.example` ships and a stock `pnpm dev:init` copies verbatim. Vitest
 * has no such define, so `resolveStage()` falls back to `dev` there and the unit
 * suites run at **50** whatever the file says. Same machine, up to two stages.
 * See the table on `listPageSize()`.
 *
 * Since 2026-09-17 a build can also override the number outright with
 * `VITE_LIST_PAGE_SIZE` (validated, `[1, 100]`, silent fallback) — that is how
 * the E2E suite gets a size small enough to page the data it already has. See
 * `parsePageSizeOverride`.
 *
 * Five things about this number are invisible from the call site:
 *
 * 1. **100 is a CEILING, not a preference.** Every consuming endpoint declares
 *    `Query(ge=1, le=100)` (`searches_list/router.py`, `imports/router.py`), so
 *    a future "make it 250" is a 422, not a bigger page. `pagination.test.ts`
 *    asserts every value in the map is `<= 100` so that lands in CI, not prod.
 *
 * 2. **The values are provisional** (Jose, 2026-09-17). The mechanism is the
 *    deliverable; 20 / 50 / 100 is a starting point and tuning them must stay a
 *    one-line edit each, in this file.
 *
 * 3. **Some surfaces deliberately do NOT consume this**, and they are not call
 *    sites the refactor forgot:
 *      - `features/imports/selection/ImportPicker.tsx` — a picker inside a
 *        dialog, sized to the dialog.
 *      - `features/imports/CompareToThriveModal.tsx`'s `CONFLICTS_PAGE_SIZE` —
 *        a transient modal list coupled to the resolve-keys budget.
 *      - `routes/client-engagement/searches/CandidatesTab.tsx` (2026-10-06;
 *        released on every stage 2026-10-07, when the Dashboard's
 *        `AllCandidatesPanel` — a non-consumer that let the server pick the
 *        size — was deleted) consumes `candidatesPageSize()` below, NOT
 *        `listPageSize()`: its own, smaller map (epic
 *        `2026-10-06-search-candidates-reports` D6 — 10 / 20 / 50, 100 per
 *        page judged too much for a cached table of up to ~1300 rows). The
 *        per-stage rule moved the choice to the web for that path, as for
 *        every other list.
 *      - Above all, the three `page_size: 100` CAP-BOUND call sites —
 *        `features/imports-export/ExportToThrivePanel.tsx`,
 *        `features/imports/CompareToThriveModal.tsx` (the `ignored` pull) and
 *        `routes/research/ai-review/useOutputFilenameSuggestion.ts`
 *        (`PROBE_PAGE_SIZE`). Those `100`s mean "the endpoint's cap — the
 *        FIRST 100 in one request", NOT "show 100 rows". None of them loops
 *        over pages, so none of them fetches "everything": past 100 rows each
 *        already under-counts (Export works around it via its "not loaded"
 *        set; Compare says so in its count; the filename probe can miss a
 *        higher `_v<N>`). Folding them in here would shrink that first page to
 *        20 on local and 50 on dev while the code still reads as a bulk pull —
 *        a silent under-count made worse. Each carries a comment saying so.
 *      - Two `const PAGE_SIZE = 100` that are NOT v2 `page_size` at all —
 *        `routes/sync-phone/SyncContacts.tsx` and
 *        `routes/closing/SendClosingEmail.tsx` send it as `count` to the LEGACY
 *        advisory-api, a different endpoint with its own bound. A grep for
 *        page-size literals in `packages/web/src` finds five, and these are the
 *        two that are not even the same protocol. Annotated at their sites.
 *
 * 4. **A page size here is load-bearing for a PRODUCT RULE.** The contacts
 *    table feeds its size into `shouldSkipPreCompareConfirmation`
 *    (`features/imports/computeCompareScope.ts`), whose rule is
 *    `totalContacts > pageSize -> show the pre-compare dialog`. Changing these
 *    numbers therefore changes when that dialog appears, per stage — a
 *    60-contact import skips it on prod (100) and gets it on dev (50) and
 *    local (20). Correct in every environment precisely because it is coupled
 *    to the real page size, and a permanent generator of "cannot reproduce on
 *    dev".
 *
 * 5. **The number may not come from the map at all.** `VITE_LIST_PAGE_SIZE`
 *    outranks every stage when it is valid, so "what does this build show?" is
 *    answered by `listPageSize()` and never by reading the map. Ask
 *    `listPageSizeOverride()` if you need to know WHICH path answered.
 */

import { type Stage, resolveStage } from "../stage/getStageLabel";

/**
 * The endpoints' `Query(ge=1, le=100)` bound, as a number the code can enforce.
 *
 * It bounds BOTH the per-stage map (asserted by `pagination.test.ts`) and the
 * `VITE_LIST_PAGE_SIZE` override below — the override is the path that can carry
 * an arbitrary string from a workflow file, so it is the one that actually needs
 * a runtime guard rather than a test.
 */
export const PAGE_SIZE_CEILING = 100;

/**
 * Optional build-time override: `VITE_LIST_PAGE_SIZE`.
 *
 * WHY IT EXISTS. The per-stage map cannot serve the E2E suite. Its numbers are
 * product choices (a recruiter's screen), while a spec needs a size small enough
 * that the data the suite ALREADY has produces more than one page — otherwise a
 * pager spec either seeds rows into a firm-wide list with concurrent writers, or
 * silently stops exercising the pager. `.github/workflows/e2e-scheduled.yml`
 * therefore sets `VITE_LIST_PAGE_SIZE=5`.
 *
 * WHY NOT AN `e2e` STAGE. `Stage` is consumed by exhaustive `Record<Stage, …>`
 * maps — real Chrome Web Store ids and listing URLs (`stage/cwsLinks.ts`, three
 * sites), the whole feature-flag registry (`web/config/featureFlags.ts`), the
 * favicon (`web/shell/setStageFavicon.ts`), this map, plus `getStageLabel`'s own
 * branches. Adding a member forces a real answer in every one of them — a CWS
 * listing and a flag registry are not things a row count should mint. Far too
 * wide a change for the purpose (Jose, 2026-09-17). (An earlier revision also
 * counted seven maps in `packages/extension`; those key a LOCAL nine-member
 * union — nucce / baby-chops / chopbook × local / dev / prod — not this `Stage`,
 * and would not have been touched.)
 *
 * VALIDATION IS TOTAL AND SILENT. Absent, empty, non-numeric, non-integer,
 * signed, zero, or past the `le=100` ceiling all fall back to the stage value.
 * This function can never throw, never return NaN and never return 0 — a typo
 * in a workflow file must not be able to brick every list in the app. The digits
 * -only regex is deliberate: bare `Number()` would accept `7.5`, `1e2`, `0x10`
 * and `+5`, none of which anyone means to write here.
 */
function parsePageSizeOverride(raw: unknown): number | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!/^[0-9]+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (value < 1 || value > PAGE_SIZE_CEILING) return null;
  return value;
}

/**
 * Read the raw override from the build-time env.
 *
 * Wrapped so the `import.meta.env` access — which only exists inside a Vite
 * build, and is `undefined` under a bare Node loader — has exactly one site, the
 * same shape `resolveStage()` uses. `packages/web/vite.config.ts` must carry
 * `VITE_LIST_PAGE_SIZE` in its `define` allowlist or this reads `undefined` in
 * the browser and the override silently does nothing.
 */
function readPageSizeOverride(): number | null {
  return parsePageSizeOverride(import.meta.env.VITE_LIST_PAGE_SIZE);
}

/**
 * Rows per page for the app's main web lists, by stage.
 *
 * Consumers: People Lists (`/research/imports`), Searches
 * (`/client-engagement/searches`) and the contacts table inside a People List
 * (`/research/imports/<id>`). All three move in lockstep by design.
 *
 * No value may exceed 100 — see point 1 in the module docstring.
 */
export const LIST_PAGE_SIZE_BY_STAGE: Record<Stage, number> = {
  local: 20,
  dev: 50,
  prod: 100,
};

/**
 * Rows per page for THIS BUILD.
 *
 * Resolution order, and there are exactly two steps:
 *
 *   1. `VITE_LIST_PAGE_SIZE`, if it is an integer in `[1, 100]`.
 *   2. otherwise `LIST_PAGE_SIZE_BY_STAGE[stage]`.
 *
 * The override wins over ANY stage, including an explicitly-passed one, because
 * it describes the build rather than the environment's product choice. A caller
 * that wants a particular stage's CONFIGURED value regardless of the override
 * should read `LIST_PAGE_SIZE_BY_STAGE[stage]` directly — which is what the
 * tests that pin the map do.
 *
 * The default argument calls `resolveStage()` so production code just writes
 * `listPageSize()`; tests pass an explicit stage rather than stubbing
 * `import.meta.env` per assertion — the same shape `getStageLabel()` already
 * uses in this package.
 *
 * Note for tests: `resolveStage()` falls back to `dev` when `VITE_APP_STAGE` is
 * unset, which is the vitest case — so unit suites run at the DEV value unless
 * they stub something. A test that re-types a number instead of calling this
 * function silently encodes the wrong stage.
 *
 * The resolutions that actually exist today. "My laptop shows N" depends on one
 * line of `.env.local`, and the unit suite beside the browser ignores that line:
 *
 *   | Where | What is set | Result |
 *   |---|---|---|
 *   | `app.local`, `.env.local` omits `VITE_APP_STAGE` or says `local` | `packages/web/vite.config.ts`: `process.env.VITE_APP_STAGE ?? env.VITE_APP_STAGE ?? "local"`, `define`d into the bundle | stage `local` -> **20** |
 *   | `app.local`, `.env.local` says `dev` (what `.env.local.example` ships and `pnpm dev:init` copies) | same line, second operand | stage `dev` -> **50** |
 *   | vitest (web, shared-fe) | no `define`; `import.meta.env.VITE_APP_STAGE` is unset | stage `dev` -> **50** |
 *   | E2E in CI | `e2e-scheduled.yml` rewrites the var to `local` AND sets `VITE_LIST_PAGE_SIZE=5` | override -> **5** |
 *   | deployed dev / prod | `ci-web.yml` sets the stage | **50** / **100** |
 *
 * "Stage `local`" is a value of an env var, not a description of a machine. Two
 * earlier revisions of this table stated the laptop row unconditionally — first
 * `dev`/50 (vitest's fallback applied to the browser), then `local`/20 (the
 * config's default, verified with `resolveConfig` on a machine whose `.env.local`
 * had been edited to `local`). Each was true of one file and false of the
 * other; the row is conditional. Note also that `VITE_LIST_PAGE_SIZE` in
 * `.env.local` reaches the build only at stage `local` (`pickEnv`); at `dev` it
 * must be exported in the shell.
 */
export function listPageSize(stage: Stage = resolveStage()): number {
  return readPageSizeOverride() ?? LIST_PAGE_SIZE_BY_STAGE[stage];
}

/**
 * Whether a valid `VITE_LIST_PAGE_SIZE` is in force, and what it is.
 *
 * Exported so a test can state WHY it expects a size, and so a spec or a debug
 * surface can say "this build is overridden" rather than inferring it from a
 * number. Returns `null` when the stage value is in force.
 */
export function listPageSizeOverride(): number | null {
  return readPageSizeOverride();
}

/**
 * Rows per page for the Candidates tab of Search Detail, by stage.
 *
 * Its own map rather than a third consumer of `LIST_PAGE_SIZE_BY_STAGE`, and
 * the difference is a product decision, not drift: the table pages a cached
 * projection of up to ~1300 rows, and *"100 per page was judged too much"*
 * (epic `2026-10-06-search-candidates-reports`, decision D6, Jose 2026-10-05).
 * So prod stops at 50 where the lists go to 100, and the lower tiers stay
 * small enough that the seeded local search (24 rows) still produces a second
 * page. `pagination.test.ts` pins every value `<= 100` (the endpoint's
 * `le=CANDIDATE_PAGE_SIZE_MAX`), monotonic, and at or below the lists' size.
 */
export const CANDIDATES_PAGE_SIZE_BY_STAGE: Record<Stage, number> = {
  local: 10,
  dev: 20,
  prod: 50,
};

/**
 * Rows per page for the Candidates tab in THIS BUILD.
 *
 * Same two-step resolution as `listPageSize()`, same override: `VITE_LIST_PAGE_SIZE`
 * wins when valid, otherwise the stage's value from `CANDIDATES_PAGE_SIZE_BY_STAGE`.
 * One knob for the whole suite is deliberate — the E2E workflow sets it to 5
 * once and every pager in the app pages the data the seed already has.
 */
export function candidatesPageSize(stage: Stage = resolveStage()): number {
  return readPageSizeOverride() ?? CANDIDATES_PAGE_SIZE_BY_STAGE[stage];
}
